import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

function ManualCallback() {
  const location = useLocation();
  const [results, setResults] = useState({
    receivedState: null,
    localState: null,
    sessionState: null,
    matches: false,
    errorMessage: null
  });
  
  useEffect(() => {
    try {
      // Parse the state from the URL
      const params = new URLSearchParams(location.search);
      const receivedState = params.get('state');
      
      // Get states from storage
      const localState = localStorage.getItem('googleOAuthState');
      const sessionState = sessionStorage.getItem('googleOAuthState');
      
      // Check if there's a match
      const matches = (receivedState === localState) || (receivedState === sessionState);
      
      // Clear the stored states (as would happen in the real callback)
      localStorage.removeItem('googleOAuthState');
      sessionStorage.removeItem('googleOAuthState');
      
      // Set the results
      setResults({
        receivedState,
        localState,
        sessionState,
        matches,
        errorMessage: null
      });
    } catch (error) {
      setResults(prev => ({
        ...prev,
        errorMessage: error.message
      }));
    }
  }, [location]);
  
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Manual OAuth Callback Test</h1>
      
      {results.errorMessage ? (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
          <p className="font-bold">Error</p>
          <p>{results.errorMessage}</p>
        </div>
      ) : (
        <div className="bg-white p-4 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold mb-4">State Parameter Check</h2>
          
          <div className="mb-4">
            <div className="grid grid-cols-3 gap-4 mb-2">
              <div className="font-semibold">Parameter</div>
              <div className="font-semibold">Value</div>
              <div className="font-semibold">Status</div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mb-2 items-center">
              <div>Received State</div>
              <code className="bg-gray-100 p-1 rounded text-sm">{results.receivedState || 'null'}</code>
              <div>{results.receivedState ? '✅' : '❌'}</div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mb-2 items-center">
              <div>localStorage State</div>
              <code className="bg-gray-100 p-1 rounded text-sm">{results.localState || 'null'}</code>
              <div>{results.localState ? (results.localState === results.receivedState ? '✅ Match' : '❌ No Match') : '❌ Missing'}</div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mb-2 items-center">
              <div>sessionStorage State</div>
              <code className="bg-gray-100 p-1 rounded text-sm">{results.sessionState || 'null'}</code>
              <div>{results.sessionState ? (results.sessionState === results.receivedState ? '✅ Match' : '❌ No Match') : '❌ Missing'}</div>
            </div>
          </div>
          
          <div className={`p-4 rounded-md ${results.matches ? 'bg-green-100' : 'bg-red-100'} mb-4`}>
            <p className="font-bold">{results.matches ? 'Success! State parameters match.' : 'Failed! No matching state found.'}</p>
            {!results.matches && (
              <p className="mt-2 text-sm">
                This is the same issue happening in your Google OAuth flow. The state parameter sent to Google
                is not being found when Google redirects back to your app.
              </p>
            )}
          </div>
          
          <div className="bg-blue-50 p-4 rounded">
            <h3 className="font-semibold mb-2">What This Means</h3>
            <p className="mb-2">
              If the test failed, your browser is not properly maintaining storage between page navigations.
              This could be due to:
            </p>
            <ul className="list-disc list-inside mb-2">
              <li>Browser privacy settings blocking storage</li>
              <li>Extensions blocking storage</li>
              <li>Cookies or storage being cleared during navigation</li>
              <li>Cross-origin issues (if testing with different domains)</li>
            </ul>
            
            <h3 className="font-semibold mt-4 mb-2">Recommended Solution</h3>
            <p>
              Since the state parameter isn't being maintained through redirects, we need to use an alternative approach like server-side session state or other persistent storage mechanisms.
            </p>
          </div>
        </div>
      )}
      
      <div className="mt-4">
        <a 
          href="/test/storage" 
          className="inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 mr-2"
        >
          Back to Storage Test
        </a>
      </div>
    </div>
  );
}

export default ManualCallback; 